/**
 * The state of whatever is in a work queue, programme or module.
 *
 * A queue mixes the two, and the row carried a `StatusBadge` — which knows
 * tenant and account statuses, not workflow ones — so every programme state
 * fell through to its pale generic "info" pill and read as a tooltip.
 *
 * @module components/shared/QueueStatusBadge
 */

import React from "react";

import { moduleReviewTone, type Tone, workflowStateTone } from "@/components/shared/status-tones";
import { TONE_BADGE, TONE_ICON } from "@/components/shared/tone-presentation";
import { Badge, Icon } from "@/components/ui";
import type { WorkflowState } from "@/server/types/workflow";

export interface QueueStatusBadgeProps {
  /** A workflow state, or a module review status. */
  status: string;
  /** `Module` reads the module review vocabulary; anything else reads workflow. */
  kind?: string;
  /**
   * How many things are in this state. Present makes the badge a tally rather
   * than a label — `Draft 5` — so a breakdown reads without a second column.
   */
  count?: number;
  size?: "xs" | "sm" | "md";
  className?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <QueueStatusBadge status={item.status} kind={item.kind} size="sm" />
 * ```
 */
/** What the tally counts, so the number is not announced on its own. */
function unitFor(kind: string | undefined, count: number): string {
  const noun = kind === undefined ? "item" : kind.toLowerCase();
  return count === 1 ? noun : `${noun}s`;
}

export function QueueStatusBadge({
  status,
  kind = undefined,
  count = undefined,
  size = "sm",
  className = "",
  "data-testid": testId,
}: QueueStatusBadgeProps): React.ReactElement {
  const tone: Tone =
    kind === "Module" ? moduleReviewTone(status) : workflowStateTone(status as WorkflowState);
  return (
    <Badge
      tone={TONE_BADGE[tone]}
      size={size}
      className={`gap-1 whitespace-nowrap ${className}`.trim()}
      data-testid={testId}
    >
      <Icon name={TONE_ICON[tone]} aria-hidden />
      {status}
      {count !== undefined && (
        <span className="font-semibold tabular-nums">
          {/* The separator and the unit are read but not drawn. The flex gap
              already spaces the number, and without them the chip announced
              as the single token `Draft4`, with nothing saying what the
              number counts. */}
          <span className="sr-only">: </span>
          {count}
          <span className="sr-only"> {unitFor(kind, count)}</span>
        </span>
      )}
    </Badge>
  );
}
